import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Mail, 
  Phone, 
  Calendar,
  MapPin,
  Clock,
  Trash2,
  RefreshCw,
  ChevronDown,
  AlertCircle
} from 'lucide-react';
import { 
  formatDate as formatDateTZ, 
  formatPreferredDate as formatPreferredDateTZ, 
  formatTimeRange 
} from '../../utils/timezoneUtils';

interface MonitoringRequest {
  id: number;
  course_ids: number[];
  course_names: string[];
  preferred_dates: string[];
  start_time: string;
  end_time: string;
  contact_email: string;
  contact_phone?: string;
  notification_method: string;
  is_active: boolean;
  created_at: string;
  expires_at: string;
}

interface RequestsResponse {
  requests: MonitoringRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function AdminRequests() {
  const [requests, setRequests] = useState<MonitoringRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        status: statusFilter
      });

      const response = await fetch(`${apiUrl}/api/admin/requests?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const result: { success: boolean; data: RequestsResponse } = await response.json();
        setRequests(result.data.requests);
        setPagination(result.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRequestStatus = async (requestId: number) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/admin/requests/${requestId}/toggle`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        // Refresh the list
        fetchRequests();
      }
    } catch (error) {
      console.error('Failed to toggle request status:', error);
    }
  };

  const cleanupExpiredRequests = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/admin/requests/cleanup`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchRequests();
      }
    } catch (error) {
      console.error('Failed to cleanup requests:', error);
    }
  };

  const filteredRequests = requests.filter(request =>
    request.contact_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.id.toString().includes(searchTerm)
  );

  const formatDate = (dateString: string) => {
    return formatDateTZ(dateString);
  };

  const formatPreferredDate = (dateString: string) => {
    return formatPreferredDateTZ(dateString);
  };


  // 计算监控请求的实际状态
  const getRequestStatus = (request: MonitoringRequest) => {
    // 使用UTC时间进行比较，确保时区一致性
    const now = new Date();
    const expiresAt = new Date(request.expires_at);
    
    if (expiresAt <= now) {
      return 'expired';
    } else if (request.is_active) {
      return 'active';
    } else {
      return 'inactive';
    }
  };

  // 获取状态显示配置
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Active',
          color: 'bg-green-100 text-green-800',
          hoverColor: 'hover:bg-green-200',
          icon: 'toggle-right'
        };
      case 'inactive':
        return {
          label: 'Inactive', 
          color: 'bg-gray-100 text-gray-800',
          hoverColor: 'hover:bg-gray-200',
          icon: 'toggle-left'
        };
      case 'expired':
        return {
          label: 'Expired',
          color: 'bg-red-100 text-red-800',
          hoverColor: 'hover:bg-red-200',
          icon: 'clock'
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-500',
          hoverColor: 'hover:bg-gray-200',
          icon: 'help'
        };
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by email or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchRequests}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <RefreshCw size={18} />
              <span>Refresh</span>
            </button>
            <button
              onClick={cleanupExpiredRequests}
              className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              <Trash2 size={18} />
              <span>Cleanup Expired</span>
            </button>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Monitoring Requests ({pagination.total} total)
          </h3>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No requests found matching your criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Golf Courses
                  </th>
                  <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preferences
                  </th>
                  <th className="w-1/10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Request #{request.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          Created: {formatDate(request.created_at)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Expires: {formatDate(request.expires_at)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Mail size={14} />
                          <span>{request.contact_email}</span>
                        </div>
                        {request.contact_phone && (
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <Phone size={14} />
                            <span>{request.contact_phone}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-500">
                          Method: {request.notification_method}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {request.course_names && request.course_names.length > 0 ? (
                          request.course_names.map((courseName, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                              <MapPin size={14} className="text-gray-400" />
                              <span className="text-gray-900">{courseName}</span>
                            </div>
                          ))
                        ) : (
                          request.course_ids.map((courseId, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                              <MapPin size={14} className="text-gray-400" />
                              <span className="text-gray-500">Course {courseId}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm">
                          <Clock size={14} className="text-gray-400" />
                          <span className="text-gray-900">{formatTimeRange(request.start_time, request.end_time)}</span>
                        </div>
                        <div className="space-y-1">
                          {request.preferred_dates.map((date, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                              <Calendar size={14} className="text-gray-400" />
                              <span className="text-gray-700">{formatPreferredDate(date)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const status = getRequestStatus(request);
                        const config = getStatusConfig(status);
                        const isExpired = status === 'expired';
                        
                        if (isExpired) {
                          // 过期请求：置灰显示，不可操作
                          return (
                            <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full opacity-60 ${config.color}`}>
                              <AlertCircle size={14} className="mr-1" />
                              <span>{config.label}</span>
                            </span>
                          );
                        } else {
                          // 正常请求：使用下拉菜单
                          return (
                            <div className="relative inline-block">
                              <select
                                value={request.is_active ? 'active' : 'inactive'}
                                onChange={(e) => {
                                  const newStatus = e.target.value;
                                  if ((newStatus === 'active' && !request.is_active) || 
                                      (newStatus === 'inactive' && request.is_active)) {
                                    toggleRequestStatus(request.id);
                                  }
                                }}
                                className={`px-3 py-1 pr-6 text-xs font-semibold rounded-full border-none outline-none appearance-none cursor-pointer transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-blue-300 ${config.color} ${config.hoverColor}`}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                              <ChevronDown size={12} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-current" />
                            </div>
                          );
                        }
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing page {pagination.page} of {pagination.totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                disabled={currentPage === pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default AdminRequests;